import { SeoHead } from '@/components/SeoHead';
import { ArrowRight, CalendarDays, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useBrowserSearch } from '@/hooks/useBrowserSearch';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { CookieConsent } from '@/components/CookieConsent';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { COMPANY } from '@/lib/company-info';
import chatRatesCrop from '@/assets/renders/chat-rates-crop-2x.webp';
import chatRatesFull from '@/assets/renders/chat-shell-answer-rates-2x.webp';
import { trackLandingCtaClicked } from '@/lib/analytics';
import { withoutAudienceEntityType } from '@/lib/auth-url';
import { buildSignupUrl, getUtmParams } from '@/lib/utm';
import { useLandingNavigationPaths } from '@/hooks/useLandingNavigationPaths';
import { useLandingSectionTracking } from '@/hooks/useLandingSectionTracking';
import { useTranslation, type TranslationKey } from '@/i18n/i18n';
import { ReadWithAI } from '@/components/ReadWithAI';

const seoContent = {
  pl: {
    title: 'Wdrożenia na silniku Bookeper — automatyzacja finansów, podatków i KSeF',
    description:
      'Niestandardowe wdrożenia na produkcyjnym silniku księgowym: KSeF dla platform billingowych (Chargebee, Stripe), FA(3) i korekty, VAT/JPK, VIES, rekonsyliacja bankowa. Dowody, nie obietnice.',
    url: 'https://www.bookeper.pl/wdrozenia',
  },
  en: {
    title: 'Custom Implementations on the Bookeper Engine — Finance, Tax & KSeF Automation',
    description:
      'Bespoke implementations on a production accounting engine: KSeF for billing platforms (Chargebee, Stripe), FA(3) and corrections, VAT/JPK, VIES, bank reconciliation. Evidence, not promises.',
    url: 'https://www.bookeper.pl/en/wdrozenia',
  },
  uk: {
    title: 'Індивідуальні впровадження на движку Bookeper — фінанси, податки та KSeF',
    description:
      'Індивідуальні впровадження на продакшн-движку бухгалтерії: KSeF для білінгових платформ (Chargebee, Stripe), FA(3) і коригування, VAT/JPK, VIES, банківська звірка.',
    url: 'https://www.bookeper.pl/uk/wdrozenia',
  },
} as const;

/**
 * Honest use-case list — every entry maps to shipped capability.
 * Do NOT add payroll (not built). See apps/landing/DESIGN.md §Site Architecture.
 */
const useCaseKeys = [
  'wdrozenia.usecase.billing',
  'wdrozenia.usecase.fa3',
  'wdrozenia.usecase.jpk',
  'wdrozenia.usecase.vies',
  'wdrozenia.usecase.bank',
  'wdrozenia.usecase.ocr',
  'wdrozenia.usecase.edeklaracje',
  'wdrozenia.usecase.zus',
] as const satisfies readonly TranslationKey[];

const flagshipScopeKeys = [
  'wdrozenia.flagship.scope1',
  'wdrozenia.flagship.scope2',
  'wdrozenia.flagship.scope3',
  'wdrozenia.flagship.scope4',
] as const satisfies readonly TranslationKey[];

const stepKeys = [
  { title: 'wdrozenia.step1.title', description: 'wdrozenia.step1.description' },
  { title: 'wdrozenia.step2.title', description: 'wdrozenia.step2.description' },
  { title: 'wdrozenia.step3.title', description: 'wdrozenia.step3.description' },
] as const;

function buildContactMailto(email: string, subject: string, search: string | null): string {
  // encodeURIComponent (not URLSearchParams): form-encoding turns spaces into
  // '+', which mail clients render literally; RFC 6068 wants %20 + CRLF.
  // null = still hydrating the prerendered page: no attribution yet.
  const attributionLines = Object.entries(search === null ? {} : getUtmParams(search)).map(
    ([key, value]) => `${key}: ${value}`
  );
  const params = [`subject=${encodeURIComponent(subject)}`];
  if (attributionLines.length > 0) {
    params.push(`body=${encodeURIComponent(['Attribution:', ...attributionLines].join('\r\n'))}`);
  }

  return `mailto:${email}?${params.join('&')}`;
}

function ContactCta({ source }: { source: string }) {
  const { t } = useTranslation();
  const search = useBrowserSearch();
  const mailto = buildContactMailto(COMPANY.email, t('wdrozenia.mailSubject'), search);
  const meetingUrl =
    search === null ? COMPANY.meetingUrl : buildSignupUrl(COMPANY.meetingUrl, search);

  // One decision, one button (Interface Craft critique: two CTAs meaning
  // "talk to us" is choice without difference). Primary = book the call;
  // e-mail is the quiet fallback line with the address as selectable text
  // (DESIGN.md mailto visible-text rule) and a real mailto on it.
  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button size="lg" className="h-14 px-7 font-bold rounded-full" asChild>
          <a
            href={meetingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackLandingCtaClicked(`${source}_meeting`, t('wdrozenia.cta'), {
                entity_type: 'implementations',
              })
            }
          >
            {t('wdrozenia.cta')}
            <CalendarDays className="ml-2 size-5" aria-hidden="true" />
          </a>
        </Button>
      </div>
      <span className="text-sm text-muted-foreground">
        {t('wdrozenia.ctaFallback')}{' '}
        <a
          href={mailto}
          className="font-mono select-all text-foreground underline underline-offset-4 hover:no-underline"
        >
          {COMPANY.email}
        </a>
      </span>
    </div>
  );
}

export default function Wdrozenia() {
  const { t, locale } = useTranslation();
  const search = useBrowserSearch();
  const seo = seoContent[locale];
  const heroRef = useLandingSectionTracking('wdrozenia_hero');
  const flagshipRef = useLandingSectionTracking('wdrozenia_flagship');
  const { withSearch } = useLandingNavigationPaths();
  // Neutral spoke: forward UTMs but drop any carried jdg/biuro entity_type so
  // an implementations prospect isn't classified into a stale signup funnel.
  // Canonical PL KSeF path only — localized /en|/uk/ksef-automatycznie rewrite to
  // /index.html (homepage metadata), so mirror the /jdg precedent and stay crawl-safe.
  const localizedKsefPath = withoutAudienceEntityType(withSearch('/ksef-automatycznie'));

  return (
    <div className="min-h-screen pt-16 md:pt-20">
      <SeoHead>
        <html lang={locale} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.url} />
        <meta property="og:type" content="website" />
        <link rel="alternate" hrefLang="pl" href={seoContent.pl.url} />
        <link rel="alternate" hrefLang="en" href={seoContent.en.url} />
        <link rel="alternate" hrefLang="uk" href={seoContent.uk.url} />
        <link rel="alternate" hrefLang="x-default" href={seoContent.pl.url} />
      </SeoHead>
      <Navigation
        cta={{
          label: t('nav.wdrozeniaCta'),
          href: search === null ? COMPANY.meetingUrl : buildSignupUrl(COMPANY.meetingUrl, search),
          trackId: 'navigation_wdrozenia',
          external: true,
        }}
      />
      <main>
        {/* Hero — engine-first framing; sells outcomes on the engine, never a dev shop */}
        <section
          ref={heroRef}
          className="px-6 md:px-8 pt-16 pb-14 bg-background border-b border-border"
          aria-labelledby="wdrozenia-hero-heading"
        >
          <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
            <div>
              <Badge variant="outline" className="mb-5">
                {t('wdrozenia.hero.badge')}
              </Badge>
              <h1
                id="wdrozenia-hero-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight max-w-3xl"
              >
                {t('wdrozenia.hero.heading')}
              </h1>
              <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
                {t('wdrozenia.hero.subheading')}
              </p>
              {/* Evidence line — true, traceable number (KSeF test env run, 2026-06) */}
              <p className="mt-4 font-mono text-sm text-foreground">
                {t('wdrozenia.hero.evidence')}
              </p>
              <div className="mt-8">
                <ContactCta source="wdrozenia_hero" />
              </div>
            </div>

            {/* Evidence anchor (real render — the engine this page sells).
                Home and /biura both anchor their heroes with an artifact;
                the empty right half read as unfinished (critique 2026-07-03). */}
            <figure className="hidden lg:block rounded-2xl border border-foreground/20 bg-card overflow-hidden shadow-sm">
              <a href={chatRatesFull} target="_blank" rel="noopener noreferrer">
                <img
                  src={chatRatesCrop}
                  alt={t('wdrozenia.hero.figureCaption')}
                  width={2260}
                  height={1080}
                  className="w-full h-auto"
                />
              </a>
              <figcaption className="p-4 text-sm text-muted-foreground leading-relaxed">
                {t('wdrozenia.hero.figureCaption')}
              </figcaption>
            </figure>
          </div>
        </section>

        <ReadWithAI page="wdrozenia" />

        {/* Flagship: KSeF for billing platforms */}
        <section
          ref={flagshipRef}
          className="px-6 md:px-8 py-14 bg-muted/40 border-b border-border"
          aria-labelledby="wdrozenia-flagship-heading"
        >
          <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-10">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
                {t('wdrozenia.flagship.kicker')}
              </p>
              <h2
                id="wdrozenia-flagship-heading"
                className="text-3xl md:text-4xl font-semibold tracking-tight"
              >
                {t('wdrozenia.flagship.heading')}
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                {t('wdrozenia.flagship.problem')}
              </p>
              <p className="mt-4 text-lg font-medium">{t('wdrozenia.flagship.pitch')}</p>
              <Link
                to={localizedKsefPath}
                className="mt-4 inline-flex items-center gap-1 text-sm underline underline-offset-4 hover:no-underline"
              >
                {t('wdrozenia.flagship.ksefLink')}
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </div>
            {/* Evidence-artifact card: the one surface that may carry a crisp border */}
            <div className="rounded-2xl border border-foreground/20 bg-background p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">
                {t('wdrozenia.flagship.scopeTitle')}
              </p>
              <ul className="space-y-3">
                {flagshipScopeKeys.map((key) => (
                  <li key={key} className="flex items-start gap-2 text-sm leading-relaxed">
                    <Check className="size-4 mt-0.5 shrink-0 text-success" aria-hidden="true" />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
              {/*
                DEFERRED — CASE STUDY SLOT. NOT dead code.
                Reason: client has not yet approved being named as a case study.
                Phase gate: BOO-3230 (client sign-off).
                Registry: docs/engineering/deferred-capabilities.md
                Linear: BOO-3230
                Do not fabricate logos, quotes, or volume claims here; the
                vitest honesty rules ban them anyway.

                <CaseStudy client="…" quote="…" metrics={…} />
              */}
            </div>
          </div>
        </section>

        {/* Use cases — honest capability list, quiet rows, no icon circles */}
        <section
          className="px-6 md:px-8 py-14 border-b border-border"
          aria-labelledby="wdrozenia-usecases-heading"
        >
          <div className="max-w-6xl mx-auto">
            <h2
              id="wdrozenia-usecases-heading"
              className="text-3xl md:text-4xl font-semibold tracking-tight"
            >
              {t('wdrozenia.usecases.heading')}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              {t('wdrozenia.usecases.subheading')}
            </p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-4">
              {useCaseKeys.map((key) => (
                <li
                  key={key}
                  className="flex items-start gap-3 border-b border-border/60 pb-4 text-base leading-relaxed"
                >
                  <span
                    className="font-mono text-xs mt-1.5 text-muted-foreground"
                    aria-hidden="true"
                  >
                    →
                  </span>
                  <span>{t(key)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* How an engagement works */}
        <section
          className="px-6 md:px-8 py-14 border-b border-border"
          aria-labelledby="wdrozenia-steps-heading"
        >
          <div className="max-w-6xl mx-auto">
            <h2
              id="wdrozenia-steps-heading"
              className="text-3xl md:text-4xl font-semibold tracking-tight"
            >
              {t('wdrozenia.steps.heading')}
            </h2>
            <ol className="mt-8 grid md:grid-cols-3 gap-8">
              {stepKeys.map((step, index) => (
                <li key={step.title}>
                  <p className="font-mono text-sm text-muted-foreground">0{index + 1}</p>
                  <h3 className="mt-2 text-xl font-semibold">{t(step.title)}</h3>
                  <p className="mt-2 text-muted-foreground leading-relaxed">
                    {t(step.description)}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Contact — capacity honesty doubles as the qualification bar */}
        <section className="px-6 md:px-8 py-16" aria-labelledby="wdrozenia-contact-heading">
          <div className="max-w-6xl mx-auto">
            <h2
              id="wdrozenia-contact-heading"
              className="text-3xl md:text-4xl font-semibold tracking-tight"
            >
              {t('wdrozenia.contact.heading')}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              {t('wdrozenia.contact.capacity')}
            </p>
            <div className="mt-8">
              <ContactCta source="wdrozenia_contact" />
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <CookieConsent />
    </div>
  );
}
